import { useRef, useEffect, MutableRefObject } from 'react';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import type { QueryFunctionContext } from 'react-query';
import { useTranslation } from 'react-i18next';

import { QUERY_KEYS } from '@24i/nxg-sdk-photon/src/enums';
import type { PageSection, Profile } from '@24i/nxg-sdk-photon';
import { log } from '@24i/nxg-core-utils/src/logger';

import { useContentData } from '../../../context/ContentData';
import { useFirebase } from '../../../context/Firebase';

import type { TimestampRecord, PendingAssetInvalidation, CleanupRefs } from './types';
import {
    debugLog,
    msToTime,
    calculateBackoffTimeMs,
    calculateMillisecondsUntilNextProgram,
    calculateRefetchInterval,
    getSectionsStructureHash,
    applyThrottling,
    updateBackoffCounter,
    fetchAndFilterPlaylists,
    processInvalidations,
    processPendingInvalidations,
    handleQuerySuccess,
    processTimestamps,
    trackDataChanges,
} from './helpers';
import { DEBUG, INITIAL_STALE_TIME, BUFFER_TIME_MS, RECENT_PAST_THRESHOLD_MS } from './constants';
import { usePauseQueries } from '../usePauseQueries';

export const getPlaylistQueryKey = (
    sectionsStructure: PageSection[],
    pageId?: string,
    selectedProfile?: Profile | null,
    language?: string
) => [
    QUERY_KEYS.playlists,
    pageId,
    getSectionsStructureHash(sectionsStructure),
    selectedProfile?.id,
    language,
];

/**
 * Custom hook for fetching and managing playlists with intelligent refetch scheduling
 *
 * @param sectionsStructure - The structure of playlist sections to fetch
 * @param pageId - Optional page identifier for caching
 * @param size - Optional number of items to fetch per page
 * @param selectedProfile - User profile for personalized content
 * @param hasScreenType - Whether we're on a screen that should use the initial stale time
 * @returns React Query's useInfiniteQuery result with playlist sections
 */

// Cleanup effect for refs

const useEffectCleanup = (refs: CleanupRefs) => {
    const {
        pendingAssetInvalidationsRef,
        pendingPlaylistInvalidationsRef,
        pendingChannelInvalidationsRef,
        prevTimestampsRef,
        targetTimeRef,
        lastInvalidationTimeRef,
        lastDataHashRef,
        lastIntervalRef,
    } = refs;

    useEffect(
        () => () => {
            // Reset array via length to 0
            pendingAssetInvalidationsRef.current.length = 0;
            pendingPlaylistInvalidationsRef.current.clear();
            pendingChannelInvalidationsRef.current.clear();
            prevTimestampsRef.current = null;
            targetTimeRef.current = null;
            Object.keys(lastInvalidationTimeRef.current).forEach(
                (key) => delete lastInvalidationTimeRef.current[key]
            );
            lastDataHashRef.current = '';
            lastIntervalRef.current = false;
            if (DEBUG) debugLog('🧹 Cleanup: Cleared ref data structures on unmount');
        },
        []
    );
};

// Data fetcher for useInfiniteQuery
const fetchPlaylistsPage = async (
    context: QueryFunctionContext,
    {
        sectionsStructure,
        queryClient,
        pendingAssetInvalidationsRef,
        pendingPlaylistInvalidationsRef,
        pendingChannelInvalidationsRef,
        lastInvalidationTimeRef,
        fetchPlaylist,
        fetchSize,
    }: {
        sectionsStructure: PageSection[];
        queryClient: ReturnType<typeof useQueryClient>;
        pendingAssetInvalidationsRef: MutableRefObject<PendingAssetInvalidation[]>;
        pendingPlaylistInvalidationsRef: MutableRefObject<Set<string>>;
        pendingChannelInvalidationsRef: MutableRefObject<Set<string>>;
        lastInvalidationTimeRef: MutableRefObject<Record<string, number>>;
        fetchPlaylist: import('./types').FetchPlaylistFn;
        fetchSize: number;
    }
) => {
    const { pageParam } = context;
    try {
        debugLog(`🔄 Fetching playlists page ${pageParam || 0}`);
        await processPendingInvalidations(
            sectionsStructure,
            queryClient,
            pendingAssetInvalidationsRef,
            pendingPlaylistInvalidationsRef,
            pendingChannelInvalidationsRef,
            lastInvalidationTimeRef
        );
        return await fetchAndFilterPlaylists(
            fetchPlaylist,
            sectionsStructure,
            fetchSize,
            pageParam || 0
        );
    } catch (error) {
        debugLog('❌ Error fetching playlist data:', error);
        throw error;
    }
};

// Query options for useInfiniteQuery
const getPlaylistQueryOptions = ({
    sectionsStructure,
    fetchSize,
    recordError,
    lastDataRefreshTimeRef,
    lastRefreshTimeRef,
    lastDataHashRef,
    lastCalculationTimeRef,
    consecutiveUnchangedFetchesRef,
    isFirstRender,
    backoffCountRef,
    calculationCountRef,
    prevTimestampsRef,
    lastIntervalRef,
    targetTimeRef,
    pendingPlaylistInvalidationsRef,
    pendingChannelInvalidationsRef,
    lastInvalidationTimeRef,
    hasScreenType,
    pauseQueries,
}) => ({
    getNextPageParam: (_lastPage, pages) =>
        pages.length < sectionsStructure.length / fetchSize ? pages.length : undefined,
    enabled: !!sectionsStructure.length && !pauseQueries,
    onError: (error) => {
        debugLog('❌ Error in playlist query:', error);
        log(error);
        recordError?.(error);
    },
    onSuccess: (data) =>
        handleQuerySuccess({
            data,
            lastDataRefreshTimeRef,
            lastRefreshTimeRef,
            lastDataHashRef,
            lastCalculationTimeRef,
            consecutiveUnchangedFetchesRef,
            isFirstRender,
        }),
    staleTime: !hasScreenType ? 0 : INITIAL_STALE_TIME,
    // Helper functions for refetchInterval to reduce cognitive complexity
    refetchInterval: (queryData) => {
        // Helper: Early return if no data
        const shouldSkip = (d: unknown) =>
            !d ||
            !(d as { pages?: unknown[] }).pages ||
            (d as { pages: unknown[] }).pages.length === 0;

        if (shouldSkip(queryData)) {
            debugLog('No data available yet for refetch calculation');
            return false;
        }

        // Helper: Check if we can reuse the last interval
        const { canReuseLastInterval, currentDataHash } = trackDataChanges({
            data: queryData,
            prevTimestamps: prevTimestampsRef.current,
            lastInterval: lastIntervalRef.current,
            lastDataHashRef,
            lastCalculationTimeRef,
            consecutiveUnchangedFetchesRef,
            isFirstRender: isFirstRender.current,
            targetTimeRef,
        });

        if (canReuseLastInterval) return lastIntervalRef.current;

        // Process timestamps and get calculation results
        const processResult = processTimestamps({
            data: queryData,
            calculationCountRef,
            lastCalculationTimeRef,
            prevTimestampsRef,
            backoffCountRef,
            lastDataHashRef,
            currentDataHash,
            lastInterval: lastIntervalRef.current,
        });

        if (processResult.shouldReturnEarly) return processResult.returnValue;

        const lowestTimestamps = processResult.timestamps;
        const result = calculateRefetchInterval(
            queryData,
            backoffCountRef.current,
            lowestTimestamps
        );

        // Helper: Log decision process if in debug mode
        const logDecision = () => {
            if (!DEBUG) return;

            if (lowestTimestamps.past !== undefined && lowestTimestamps.future !== undefined) {
                debugLog('🧠 Decision: Both past and future timestamps found');
                const backoffMs = calculateBackoffTimeMs(backoffCountRef.current);
                const futureMs =
                    calculateMillisecondsUntilNextProgram(lowestTimestamps.future, new Date()) ||
                    Infinity;
                debugLog(
                    `🧠 Compare: Backoff time (${msToTime(backoffMs)}) vs Future time (${msToTime(
                        futureMs + BUFFER_TIME_MS
                    )})`
                );
                debugLog(
                    result.usedFutureTime
                        ? '🧠 Selected: Using future timestamp strategy (will reset backoff)'
                        : '🧠 Selected: Using backoff strategy (will increment backoff count)'
                );
            } else if (lowestTimestamps.past !== undefined) {
                debugLog('🧠 Decision: Only past timestamp found, using backoff strategy');
            } else if (lowestTimestamps.future !== undefined) {
                debugLog('🧠 Decision: Only future timestamp found, scheduling for future time');
            }
        };

        // Log decision process
        logDecision();

        // Update backoff counter based on result
        updateBackoffCounter(
            backoffCountRef,
            lowestTimestamps.past,
            result.usedFutureTime,
            typeof result.interval === 'number'
        );

        // Helper: Update interval based on throttling and invalidations
        const updateInterval = (interval: number, usedFutureTime: boolean) => {
            const playlistId = usedFutureTime
                ? lowestTimestamps.futurePlaylistId
                : lowestTimestamps.pastPlaylistId;
            const channelId = usedFutureTime
                ? lowestTimestamps.futureChannelId
                : lowestTimestamps.pastChannelId;

            let updatedInterval = interval;
            updatedInterval = applyThrottling(
                updatedInterval,
                usedFutureTime,
                consecutiveUnchangedFetchesRef.current
            );

            updatedInterval = processInvalidations(
                playlistId,
                channelId,
                consecutiveUnchangedFetchesRef.current,
                pendingPlaylistInvalidationsRef,
                pendingChannelInvalidationsRef,
                lastInvalidationTimeRef,
                updatedInterval
            );

            return updatedInterval;
        };

        // Calculate final interval result
        let intervalResult = result.interval;
        if (typeof intervalResult === 'number') {
            intervalResult = updateInterval(intervalResult, result.usedFutureTime);
        }

        // Update refs with calculated interval - use local copies to avoid param reassignment
        const localIntervalRef = lastIntervalRef;
        localIntervalRef.current = intervalResult;

        const localTargetTimeRef = targetTimeRef;
        localTargetTimeRef.current =
            typeof intervalResult === 'number' ? Date.now() + intervalResult : null;

        // Helper: Log final interval info
        const logIntervalInfo = () => {
            if (!DEBUG) return;

            if (typeof intervalResult !== 'number') {
                debugLog('⏹️ No refetch scheduled');
                return;
            }

            const now = Date.now();
            const targetTime = targetTimeRef.current ?? now + intervalResult;
            const nextIntervalCheckTime = new Date(targetTime);
            const source = result.usedFutureTime ? 'future' : 'past';

            const getAssetInfo = (channelId?: string | null, playlistId?: string | null) =>
                [
                    channelId ? `channel: ${channelId}` : '',
                    playlistId ? `playlist: ${playlistId}` : '',
                ]
                    .filter(Boolean)
                    .join(', ');

            const isVeryRecentPast =
                lowestTimestamps.past &&
                now - lowestTimestamps.past * 1000 <= RECENT_PAST_THRESHOLD_MS;

            const intervalInfo = `⌚ Next interval check in ${msToTime(
                (targetTimeRef.current ?? now) - now
            )} at ${nextIntervalCheckTime.toLocaleTimeString('en-US', { hour12: false })}`;

            if (isVeryRecentPast && !result.usedFutureTime && lowestTimestamps.past) {
                const assetInfo = getAssetInfo(
                    lowestTimestamps.pastChannelId,
                    lowestTimestamps.pastPlaylistId
                );
                const pastMinutesAgo = Math.round(
                    (now - lowestTimestamps.past * 1000) / (60 * 1000)
                );
                debugLog(
                    `${intervalInfo} (PRIORITY REFRESH for broadcast from ${pastMinutesAgo}m ago, ${assetInfo})`
                );
            } else if (result.usedFutureTime && lowestTimestamps.future) {
                const assetInfo = getAssetInfo(
                    lowestTimestamps.futureChannelId,
                    lowestTimestamps.futurePlaylistId
                );
                debugLog(`${intervalInfo} (using ${source} timestamp, ${assetInfo})`);
            } else if (!result.usedFutureTime && lowestTimestamps.past) {
                const assetInfo = getAssetInfo(
                    lowestTimestamps.pastChannelId,
                    lowestTimestamps.pastPlaylistId
                );
                debugLog(`${intervalInfo} (using ${source} timestamp, ${assetInfo})`);
            } else {
                debugLog(`${intervalInfo} (using ${source} timestamp)`);
            }
        };

        // Log interval info
        logIntervalInfo();

        return intervalResult;
    },
});

const usePlaylistsQuery = (
    sectionsStructure: PageSection[],
    pageId?: string,
    size?: number,
    selectedProfile?: Profile | null,
    hasScreenType = false
) => {
    const { fetchPlaylist } = useContentData();
    const { i18n } = useTranslation();
    const { language } = i18n;
    const fetchSize = size || sectionsStructure.length;
    const { recordError } = useFirebase();
    const queryClient = useQueryClient();
    const pauseQueries = usePauseQueries();

    // Refs
    const backoffCountRef = useRef(0);
    const lastCalculationTimeRef = useRef(0);
    const lastRefreshTimeRef = useRef(0);
    const lastDataRefreshTimeRef = useRef(0);
    const lastDataHashRef = useRef('');
    const lastIntervalRef = useRef<number | false>(false);
    const targetTimeRef = useRef<number | null>(null);
    const prevTimestampsRef = useRef<TimestampRecord | null>(null);
    const calculationCountRef = useRef(0);
    const isFirstRender = useRef(true);
    const consecutiveUnchangedFetchesRef = useRef(0);
    const pendingAssetInvalidationsRef: MutableRefObject<PendingAssetInvalidation[]> = useRef([]);
    const pendingPlaylistInvalidationsRef = useRef<Set<string>>(new Set());
    const pendingChannelInvalidationsRef = useRef<Set<string>>(new Set());
    const lastInvalidationTimeRef = useRef<Record<string, number>>({});

    // Cleanup effect for refs
    useEffectCleanup({
        pendingAssetInvalidationsRef,
        pendingPlaylistInvalidationsRef,
        pendingChannelInvalidationsRef,
        prevTimestampsRef,
        targetTimeRef,
        lastInvalidationTimeRef,
        lastDataHashRef,
        lastIntervalRef,
    });

    // Data fetcher
    const fetcher = (context: QueryFunctionContext) =>
        fetchPlaylistsPage(context, {
            sectionsStructure,
            queryClient,
            pendingAssetInvalidationsRef,
            pendingPlaylistInvalidationsRef,
            pendingChannelInvalidationsRef,
            lastInvalidationTimeRef,
            fetchPlaylist,
            fetchSize,
        });

    // Query options
    const queryOptions = getPlaylistQueryOptions({
        sectionsStructure,
        fetchSize,
        recordError,
        lastDataRefreshTimeRef,
        lastRefreshTimeRef,
        lastDataHashRef,
        lastCalculationTimeRef,
        consecutiveUnchangedFetchesRef,
        isFirstRender,
        backoffCountRef,
        calculationCountRef,
        prevTimestampsRef,
        lastIntervalRef,
        targetTimeRef,
        pendingPlaylistInvalidationsRef,
        pendingChannelInvalidationsRef,
        lastInvalidationTimeRef,
        hasScreenType,
        pauseQueries,
    });

    return useInfiniteQuery<PageSection[]>(
        getPlaylistQueryKey(sectionsStructure, pageId, selectedProfile, language),
        fetcher,
        queryOptions
    );
};

export default usePlaylistsQuery;
